<template>
  <div>
    <component
      :is="isOverlay ? 'div' : 'v-card'"
      :class="[isOverlay ? 'agent-editor-surface agent-editor-surface--overlay bg-background' : 'ma-0 pa-2 card bg-background']"
      v-bind="isOverlay ? {} : { color: 'background', density: 'compact', variant: 'elevated', elevation: 4 }"
    >
    <v-form density="compact" @submit.prevent>
      <div v-if="isOverlay && show" class="agent-editor-header">
        <div class="d-flex align-center w-100">
          <v-menu v-model="showIconPicker" :close-on-content-click="false" location="bottom start" offset="10">
            <template #activator="{ props }">
              <div class="mr-3 agent-icon-picker-anchor">
                <v-avatar size="36" rounded="lg" class="agent-icon-tile">
                  <v-icon :icon="displayIcon" size="small"></v-icon>
                </v-avatar>
                <v-btn
                  v-bind="props"
                  color="primary"
                  icon="mdi-pencil-outline"
                  size="x-small"
                  variant="flat"
                  class="agent-icon-picker-trigger"
                  @click.stop
                />
              </div>
            </template>

            <v-card class="agent-icon-picker-menu" color="background" @click.stop>
              <div class="agent-icon-picker-grid">
                <v-btn
                  v-for="preset in iconPresets"
                  :key="preset"
                  :color="iconSelectionValue === preset ? 'primary' : 'default'"
                  :variant="iconSelectionValue === preset ? 'tonal' : 'text'"
                  icon
                  size="small"
                  class="agent-icon-option"
                  @click.stop.prevent="selectIconPreset(preset)"
                >
                  <v-icon :icon="preset" size="small"></v-icon>
                </v-btn>
              </div>

              <div class="agent-icon-picker-divider"></div>

              <div class="agent-icon-picker-label">Custom MDI Name</div>
              <v-text-field
                v-model="iconInput"
                density="compact"
                variant="outlined"
                hide-details
                class="mt-2"
                clearable
                @blur="commitIconInput"
                @keydown.enter.prevent="commitIconInput"
              >
                <template #prepend-inner>
                  <v-icon :icon="displayIcon" color="primary"></v-icon>
                </template>
              </v-text-field>

              <a
                class="agent-icon-picker-link"
                :href="mdiBrowseUrl"
                target="_blank"
                rel="noopener noreferrer"
                @click.stop
              >
                Browse all MDI icons
                <v-icon icon="mdi-arrow-right" size="x-small" class="ml-1"></v-icon>
              </a>
            </v-card>
          </v-menu>
          <div style="min-width: 0;">
            <div class="d-flex align-center">
              <div class="text-subtitle-1 text-truncate">{{ name || 'New Agent' }}</div>
              <v-chip v-if="isPublished" color="success" size="x-small" class="ml-2">Live</v-chip>
            </div>
          </div>
        </div>
      </div>
      <v-alert v-if="isShared && sharedAgent" type="info" variant="tonal" class="ma-3 shared-agent-banner">
        Shared by @{{ sharedAgent.ownerUsername }} — name and model are owner-controlled.
      </v-alert>
      <v-expand-transition>
        <div v-if="isOverlay && show" class="agent-overlay-layout">
          <v-row class="ma-0 px-4 pb-3 align-stretch flex-nowrap agent-overlay-row">
            <v-col cols="5" class="pa-0 pr-4 agent-overlay-settings">
              <v-list density="compact" class="ma-0 pa-0 bg-background">
                <v-list-item density="compact" :class="showGlobalTooltips ? 'my-0 ml-6 mr-0 pa-0 mt-1' : 'ma-0 pa-0 mt-1'">
                  <v-tooltip v-if="showGlobalTooltips" position="top" max-width="300"
                    text="The agent name should be a one to 2 word description. Like 'Planner' or 'Coder'.">
                    <template v-slot:activator="{ props }">
                      <v-icon v-bind="props" size="x-small"
                        class="global-tooltip-icon mt-5 ml-n6">mdi-information-outline</v-icon>
                    </template>
                  </v-tooltip>
                  <v-text-field v-model="name" @blur="trackFieldUpdate('agent_name')" class="mt-2"
                    label="Agent Name" :readonly="isShared"
                    :rules="isShared ? [] : [() => !!name || 'Agent name is required']"
                    density="compact" variant="outlined" />
                </v-list-item>
                <v-list-item density="compact" :class="showGlobalTooltips ? 'my-0 ml-6 mr-0 pa-0 mt-1' : 'ma-0 pa-0 mt-1'">
                  <v-tooltip v-if="showGlobalTooltips" position="top" max-width="300" text="The agent description should be a short sentance on what the agent does. Use the agent's name in the
                    description if you can. Do not use 'You'. This description is used by the mission leader in completing the
                    mission and is also used if you choose to autofill a squad's workflow.">
                    <template v-slot:activator="{ props }">
                      <v-icon v-bind="props" size="x-small"
                        class="global-tooltip-icon mt-5 ml-n6">mdi-information-outline</v-icon>
                    </template>
                  </v-tooltip>
                  <v-text-field v-model="description" @blur="trackFieldUpdate('agent_description')" class="mt-2"
                    label="Agent Description" :rules="[() => !!description || 'Agent description is required']" density="compact"
                    variant="outlined" />
                </v-list-item>
                <v-list-item density="compact" :class="showGlobalTooltips ? 'my-0 ml-6 mr-0 pa-0 mt-1' : 'ma-0 pa-0 mt-1'">
                  <v-tooltip v-if="showGlobalTooltips" position="top" max-width="300"
                    text="Choose a model that you have saved under the Models tab.">
                    <template v-slot:activator="{ props }">
                      <v-icon v-bind="props" size="x-small"
                        class="global-tooltip-icon mt-5 ml-n6">mdi-information-outline</v-icon>
                    </template>
                  </v-tooltip>
                  <v-select v-if="!isShared" v-model="model" @blur="modelSelected" :items="modelChoices" class="mt-2" item-title="name" item-value="id"
                    :return-object="false" label="Agent Model" variant="outlined"
                    :rules="[() => !!model || 'You must choose a model for your agent to use.']" density="compact" />
                  <v-text-field v-else :model-value="sharedOwnerModelName" class="mt-2"
                    label="Agent Model (owner-controlled)" readonly density="compact" variant="outlined" />
                </v-list-item>
                <v-list-item density="compact" :class="showGlobalTooltips ? 'my-0 ml-6 mr-0 pa-0 mt-1' : 'ma-0 pa-0 mt-1'">
                  <v-tooltip v-if="showGlobalTooltips" position="top" max-width="300"
                    text="Choose a saved voice alias to speak this agent's replies. Leave blank to disable TTS.">
                    <template v-slot:activator="{ props }">
                      <v-icon v-bind="props" size="x-small"
                        class="global-tooltip-icon mt-5 ml-n6">mdi-information-outline</v-icon>
                    </template>
                  </v-tooltip>
                  <v-select v-model="voiceSavedName" :items="savedVoiceNames" label="Agent Voice (optional)" class="mt-2"
                    variant="outlined" density="compact" clearable />
                </v-list-item>
                <v-list-item density="compact" :class="showGlobalTooltips ? 'my-0 ml-6 mr-0 pa-0 mb-n1' : 'ma-0 pa-0 mb-n1'">
                  <v-tooltip v-if="showGlobalTooltips" position="top" max-width="300"
                    text="Enable the agent to use tools to perform tasks.">
                    <template v-slot:activator="{ props }">
                      <v-icon v-bind="props" size="x-small"
                        class="global-tooltip-icon mt-5 ml-n6">mdi-information-outline</v-icon>
                    </template>
                  </v-tooltip>
                  <v-checkbox v-model="enableTools" @update:modelValue="toggleToolsUsage" label="Use Tools" hide-details density="compact"
                    class="display-inline my-0 py-0" />
                </v-list-item>
                <v-list-item v-if="enableTools" density="compact"
                  :class="showGlobalTooltips ? 'my-0 ml-6 mr-0 pa-0 mb-n1' : 'ma-0 pa-0 mb-n1'">
                  <v-tooltip v-if="showGlobalTooltips" position="top" max-width="300"
                    text="List of tools that the agent can use">
                    <template v-slot:activator="{ props }">
                      <v-icon v-bind="props" size="x-small"
                        class="global-tooltip-icon mt-5 ml-n6">mdi-information-outline</v-icon>
                    </template>
                  </v-tooltip>
                  <v-select v-model="selectedTools" @update:modelValue="onToolSelectionChange" :items="toolsNameList"
                    label="Tools" multiple chips density="compact" variant="outlined" class="mt-2 mb-n1" />
                </v-list-item>
                <v-list-item v-if="enableTools && selectedTools.length > 0" density="compact" class="py-0 my-0">
                  <v-btn variant="plain" @click="openFunctionSelectionDialog" size="small" class="ml-n2">
                    Select Functions ({{ selectedFunctionsCount }} selected)
                  </v-btn>
                </v-list-item>
                <v-list-item density="compact" :class="showGlobalTooltips ? 'my-0 ml-6 mr-0 pa-0 mb-n1' : 'ma-0 pa-0 mb-n1'">
                  <v-tooltip v-if="showGlobalTooltips" position="top" max-width="300"
                    text="Enable the agent to look up information in data you've uploaded via the Embeddings tab.">
                    <template v-slot:activator="{ props }">
                      <v-icon v-bind="props" size="x-small"
                        class="global-tooltip-icon mt-5 ml-n6">mdi-information-outline</v-icon>
                    </template>
                  </v-tooltip>
                  <v-checkbox v-model="enableEmbeddings" @update:modelValue="toggleEmbeddingsUsage"
                    label="Use Embeddings (RAG)" hide-details density="compact" class="display-inline my-0 py-0" />
                </v-list-item>
                <v-list-item v-if="enableEmbeddings" density="compact"
                  :class="showGlobalTooltips ? 'my-0 ml-6 mr-0 pa-0 mb-n1' : 'ma-0 pa-0 mb-n1'">
                  <v-tooltip v-if="showGlobalTooltips" position="top" max-width="300"
                    text="Datasets to include in the RAG (Retrieval-Augmented Generation) process. These are the datasets you've uploaded via the Embeddings tab.">
                    <template v-slot:activator="{ props }">
                      <v-icon v-bind="props" size="x-small"
                        class="global-tooltip-icon mt-5 ml-n6">mdi-information-outline</v-icon>
                    </template>
                  </v-tooltip>
                  <v-select v-model="selectedEmbeddings" @update:modelValue="onEmbeddingCollectionChange"
                    :items="embeddingsList" item-value="safeName" class="mt-2 mb-n1" multiple chips item-title="name"
                    label="Embeddings to include (RAG)" variant="outlined" density="compact" />
                </v-list-item>
                <v-list-item v-if="enableEmbeddings && showAdvanced" density="compact"
                  :class="showGlobalTooltips ? 'my-0 ml-6 mr-0 pa-0 mb-n1' : 'ma-0 pa-0 mb-n1'">
                  <v-tooltip v-if="showGlobalTooltips" position="top" max-width="300"
                    text="The embedding specific model to use when looking up information you've uploaded. Don't change this unless you know what it is.">
                    <template v-slot:activator="{ props }">
                      <v-icon v-bind="props" size="x-small"
                        class="global-tooltip-icon mt-5 ml-n6">mdi-information-outline</v-icon>
                    </template>
                  </v-tooltip>
                  <v-select v-model="embeddingModel" @update:modelValue="onEmbeddingModelChange"
                    :items="effectiveEmbeddingModelConfigList" item-title="name" item-value="id" label="Embedding Model"
                    :rules="[() => (!!embeddingModel && enableEmbeddings) || 'Embedding Model is required.']"
                    density="compact" variant="outlined" class="display-block mt-2 mb-n1" />
                </v-list-item>
                <v-list-item v-if="enableEmbeddings" density="compact"
                  :class="showGlobalTooltips ? 'my-0 ml-6 mr-0 pa-0 mb-n1' : 'ma-0 pa-0 mb-n1'">
                  <v-tooltip v-if="showGlobalTooltips" position="top" max-width="300"
                    text="This corresponds to the type of embeddings you're including and how you want the model to use them. Meta-prompts specific to these options are in your global config. Open = allow the agent to use it's own knowledge in addition, Closed = only use the embeddings, Code = specific prompting for code use and generation.">
                    <template v-slot:activator="{ props }">
                      <v-icon v-bind="props" size="x-small"
                        class="global-tooltip-icon mt-5 ml-n6">mdi-information-outline</v-icon>
                    </template>
                  </v-tooltip>
                  <v-select v-model="embeddingType" @update:modelValue="onEmbeddingTypeChange" :items="embeddingTypes"
                    class="mt-2" item-title="name" label="Embedding type" variant="outlined" density="compact" />
                </v-list-item>
                <v-list-item v-if="!isShared && canPublishAgents" density="compact"
                  :class="showGlobalTooltips ? 'my-0 ml-6 mr-0 pa-0 mb-n1' : 'ma-0 pa-0 mb-n1'">
                  <v-tooltip v-if="showGlobalTooltips" position="top" max-width="300"
                    text="Make this agent publicly accessible via a unique URL">
                    <template v-slot:activator="{ props }">
                      <v-icon v-bind="props" size="x-small" class="global-tooltip-icon mt-5 ml-n6">
                        mdi-information-outline
                      </v-icon>
                    </template>
                  </v-tooltip>
                  <div class="d-flex align-center">
                    <v-checkbox v-model="isPublished" @change="togglePublish" label="Publish Agent" hide-details density="compact"
                      class="display-inline my-0 py-0" />
                    <Button v-if="show" color="primary" buttonClass="ml-2" :click="showShareDialog"
                      icon="mdi-share-variant" size="medium" tooltip="Share Agent" />
                    <v-tooltip v-if="isPublished" text="Open Public Page" location="top">
                      <template v-slot:activator="{ props }">
                        <v-btn v-bind="props" color="secondary" icon="mdi-open-in-new" size="small"
                          class="ml-1 d-inline-flex agent-link" :href="publicUrl" @click="openPublicUrl"
                          variant="plain" target="_blank" />
                      </template>
                    </v-tooltip>
                    <v-tooltip v-if="isPublished && publishedSlug" text="Chat with Agent" location="top">
                      <template v-slot:activator="{ props }">
                        <v-btn v-bind="props" color="primary" icon="mdi-chat-outline" size="small"
                          class="ml-0 d-inline-flex agent-link" variant="plain"
                          @click="goToChat" />
                      </template>
                    </v-tooltip>
                  </div>
                </v-list-item>
                <v-list-item v-if="!isShared && isPublished && publicUrl" density="compact" class="ma-0 pa-0 mt-2">
                  <v-text-field :model-value="publicUrl" label="Public URL" readonly density="compact" variant="outlined" class="mt-2"
                    append-inner-icon="mdi-content-copy" @click:append-inner="copyPublicUrl" />
                </v-list-item>
                <v-list-item v-if="showAdvanced" density="compact" class="ma-0 pa-0 mt-1">
                  <ModelOptionsChoices
                    @update="updateOptions"
                    :modelOptionsInput="modelOptions"
                    :modelName="modelConfig?.model || ''"
                  />
                </v-list-item>
              </v-list>
            </v-col>
            <v-col cols="7" class="pa-0 pl-4 d-flex flex-column agent-overlay-prompt-column">
              <v-textarea
                v-model="systemPrompt"
                @blur="trackFieldUpdate('agent_prompt')"
                :loading="loading"
                :disabled="loading"
                class="mt-2 hide-scrollbar flex-grow-1 agent-overlay-prompt"
                label="Agent Prompt"
                variant="outlined"
                :rules="[() => !!systemPrompt || 'Agent prompt is required']"
                density="compact"
                rows="1"
                no-resize
                hide-details
              >
                <template v-slot:append-inner>
                  <div>
                    <v-tooltip text="Autofill"
                      v-if="name != '' && description != '' && model != null">
                      <template v-slot:activator="{ props }">
                        <v-icon class="autocomplete-icon" v-bind="props" color="primary" icon="mdi-lightning-bolt-outline"
                          @click="autofillPrompt()" />
                      </template>
                    </v-tooltip>
                  </div>
                </template>
              </v-textarea>
            </v-col>
          </v-row>
        </div>
        <v-list v-else density="compact" class="ma-0 pa-0 card-content bg-background" v-show="show">
          <v-list-item v-if="!isOverlay" density="compact" class="ma-0 pa-0">
            <div class="d-flex align-center w-100 agent-editor-header">
              <v-menu v-model="showIconPicker" :close-on-content-click="false" location="bottom start" offset="10">
                <template #activator="{ props }">
                  <div class="mr-3 agent-icon-picker-anchor">
                    <v-avatar size="36" rounded="lg" class="agent-icon-tile">
                      <v-icon :icon="displayIcon" size="small"></v-icon>
                    </v-avatar>
                    <v-btn
                      v-bind="props"
                      color="primary"
                      icon="mdi-pencil-outline"
                      size="x-small"
                      variant="flat"
                      class="agent-icon-picker-trigger"
                      @click.stop
                    />
                  </div>
                </template>

                <v-card class="agent-icon-picker-menu" color="background" @click.stop>
                  <div class="agent-icon-picker-grid">
                    <v-btn
                      v-for="preset in iconPresets"
                      :key="preset"
                      :color="iconSelectionValue === preset ? 'primary' : 'default'"
                      :variant="iconSelectionValue === preset ? 'tonal' : 'text'"
                      icon
                      size="small"
                      class="agent-icon-option"
                      @click.stop.prevent="selectIconPreset(preset)"
                    >
                      <v-icon :icon="preset" size="small"></v-icon>
                    </v-btn>
                  </div>

                  <div class="agent-icon-picker-divider"></div>

                  <div class="agent-icon-picker-label">Custom MDI Name</div>
                  <v-text-field
                    v-model="iconInput"
                    density="compact"
                    variant="outlined"
                    hide-details
                    class="mt-2"
                    clearable
                    @blur="commitIconInput"
                    @keydown.enter.prevent="commitIconInput"
                  >
                    <template #prepend-inner>
                      <v-icon :icon="displayIcon" color="primary"></v-icon>
                    </template>
                  </v-text-field>

                  <a
                    class="agent-icon-picker-link"
                    :href="mdiBrowseUrl"
                    target="_blank"
                    rel="noopener noreferrer"
                    @click.stop
                  >
                    Browse all MDI icons
                    <v-icon icon="mdi-arrow-right" size="x-small" class="ml-1"></v-icon>
                  </a>
                </v-card>
              </v-menu>
              <div style="min-width: 0;">
                <div class="d-flex align-center">
                  <div class="text-subtitle-1 text-truncate">{{ name || 'New Agent' }}</div>
                  <v-chip v-if="isPublished" color="success" size="x-small" class="ml-2">Live</v-chip>
                </div>
                <div class="text-caption text-medium-emphasis">{{ icon || defaultAgentIcon }}</div>
              </div>
            </div>
          </v-list-item>
          <v-list-item density="compact" :class="showGlobalTooltips ? 'my-0 ml-6 mr-0 pa-0 mt-1' : 'ma-0 pa-0 mt-1'">
            <v-tooltip v-if="showGlobalTooltips" position="top" max-width="300"
              text="The agent name should be a one to 2 word description. Like 'Planner' or 'Coder'.">
              <template v-slot:activator="{ props }">
                <v-icon v-bind="props" size="x-small"
                  class="global-tooltip-icon mt-5 ml-n6">mdi-information-outline</v-icon>
              </template>
            </v-tooltip>
            <v-text-field v-model="name" @blur="trackFieldUpdate('agent_name')" label="Agent Name" :class="isOverlay ? 'mt-2' : 'pt-2'"
              :readonly="isShared"
              :rules="isShared ? [] : [() => !!name || 'Agent name is required']" density="compact" variant="outlined" />
          </v-list-item>
          <v-list-item density="compact" :class="showGlobalTooltips ? 'my-0 ml-6 mr-0 pa-0 mt-1' : 'ma-0 pa-0 mt-1'">
            <v-tooltip v-if="showGlobalTooltips" position="top" max-width="300" text="The agent description should be a short sentance on what the agent does. Use the agent's name in the
              description if you can. Do not use 'You'. This description is used by the mission leader in completing the
              mission and is also used if you choose to autofill a squad's workflow.">
              <template v-slot:activator="{ props }">
                <v-icon v-bind="props" size="x-small"
                  class="global-tooltip-icon mt-5 ml-n6">mdi-information-outline</v-icon>
              </template>
            </v-tooltip>
            <v-text-field v-model="description" @blur="trackFieldUpdate('agent_description')" label="Agent Description"
              :class="isOverlay ? 'mt-2' : 'pt-2'" :rules="[() => !!description || 'Agent description is required']" density="compact"
              variant="outlined" />
          </v-list-item>
          <v-list-item density="compact" :class="[showGlobalTooltips ? 'my-0 ml-6 mr-0 pa-0 mt-1 h-100' : 'ma-0 pa-0 mt-1', { 'agent-prompt-item': isOverlay }]">
            <v-tooltip v-if="showGlobalTooltips" position="top" max-width="300" text="The agent prompt should be a longer set of instructions on exactly what the agent's job is. Include what
              it should do and what it shouldn't do. You can attempt to autofill this field, but results will vary.">
              <template v-slot:activator="{ props }">
                <v-icon v-bind="props" size="x-small"
                  class="global-tooltip-icon mt-5 ml-n6">mdi-information-outline</v-icon>
              </template>
            </v-tooltip>
            <v-textarea v-if="!isPromptExpanded" v-model="systemPrompt" @blur="trackFieldUpdate('agent_prompt')"
              :loading="loading" :disabled="loading" :class="[isOverlay ? 'mt-2 h-100' : 'pt-2', 'hide-scrollbar']" label="Agent Prompt" variant="outlined"
              :rules="[() => !!systemPrompt || 'Agent prompt is required']" density="compact" :rows="isOverlay ? 1 : 5" no-resize hide-details>
              <template v-slot:append-inner>
                <div>
                  <v-icon v-if="!isOverlay" icon="mdi-arrow-expand" color="primary"
                    @click="togglePromptExpansion" class="expand-prompt-icon" style="cursor: pointer;"></v-icon>
                  <v-tooltip text="Autofill"
                    v-if="name != '' && description != '' && model != null">
                    <template v-slot:activator="{ props }">
                      <v-icon class="autocomplete-icon" v-bind="props" color="primary" icon="mdi-lightning-bolt-outline"
                        @click="autofillPrompt()" />
                    </template>
                  </v-tooltip>
                </div>
              </template>
            </v-textarea>
          </v-list-item>
          <!-- <v-list-item density="compact" :class="showGlobalTooltips ? 'my-0 ml-6 mr-0 pa-0' : 'ma-0 pa-0'">
            <v-tooltip v-if="showGlobalTooltips" position="top" max-width="300" text="Automatically send this agent's context to another agent. The other agent must be in the same squad.">
              <template v-slot:activator="{ props }">
                <v-icon v-bind="props" size="x-small" class="global-tooltip-icon mt-5 ml-n6">mdi-information-outline</v-icon>
              </template>
            </v-tooltip>
            <v-select v-model="sendContextToAgent" @blur="checkHash" :items="agentsList" class="pt-2" item-title="name"
              label="Send context to other agent?" variant="outlined" density="compact" />
          </v-list-item> -->
          <v-list-item density="compact" :class="showGlobalTooltips ? 'my-0 ml-6 mr-0 pa-0 mt-1' : 'ma-0 pa-0 mt-1'">
            <v-tooltip v-if="showGlobalTooltips" position="top" max-width="300"
              text="Choose a model that you have saved under the Models tab.">
              <template v-slot:activator="{ props }">
                <v-icon v-bind="props" size="x-small"
                  class="global-tooltip-icon mt-5 ml-n6">mdi-information-outline</v-icon>
              </template>
            </v-tooltip>
            <v-select v-if="!isShared" v-model="model" @blur="modelSelected" :items="modelChoices" :class="isOverlay ? 'mt-2' : 'pt-2'" item-title="name" item-value="id"
              :return-object="false" label="Agent Model" variant="outlined"
              :rules="[() => !!model || 'You must choose a model for your agent to use.']" density="compact" />
            <v-text-field v-else :model-value="sharedOwnerModelName" :class="isOverlay ? 'mt-2' : 'pt-2'"
              label="Agent Model (owner-controlled)" readonly density="compact" variant="outlined" />
          </v-list-item>

          <!-- NEW: Agent Voice (optional) -->
          <v-list-item density="compact" :class="showGlobalTooltips ? 'my-0 ml-6 mr-0 pa-0 mt-1' : 'ma-0 pa-0 mt-1'">
            <v-tooltip v-if="showGlobalTooltips" position="top" max-width="300"
              text="Choose a saved voice alias to speak this agent's replies. Leave blank to disable TTS.">
              <template v-slot:activator="{ props }">
                <v-icon v-bind="props" size="x-small"
                  class="global-tooltip-icon mt-5 ml-n6">mdi-information-outline</v-icon>
              </template>
            </v-tooltip>
            <v-select v-model="voiceSavedName" :items="savedVoiceNames" label="Agent Voice (optional)" :class="isOverlay ? 'mt-2' : 'pt-2'"
              variant="outlined" density="compact" clearable />
          </v-list-item>

          <!-- NEW: Processor Agent (optional) -->
          <!-- <v-list-item density="compact" :class="showGlobalTooltips ? 'my-0 ml-6 mr-0 pa-0' : 'ma-0 pa-0'">
            <v-tooltip v-if="showGlobalTooltips" position="top" max-width="300"
              text="If set, the primary agent's final text is sent to this processor agent first. The processor's result is what will be spoken.">
              <template v-slot:activator="{ props }">
                <v-icon v-bind="props" size="x-small"
                  class="global-tooltip-icon mt-5 ml-n6">mdi-information-outline</v-icon>
              </template>
            </v-tooltip>
            <v-select v-model="responseProcessorAgent" :items="processorAgentChoices" label="Processor Agent (optional)"
              class="pt-2" variant="outlined" density="compact" clearable />
          </v-list-item> -->
          <!-- Tools -->
          <v-list-item density="compact" :class="showGlobalTooltips ? 'my-0 ml-6 mr-0 pa-0 mb-n1' : 'ma-0 pa-0 mb-n1'">
            <v-tooltip v-if="showGlobalTooltips" position="top" max-width="300"
              text="Enable the agent to use tools to perform tasks.">
              <template v-slot:activator="{ props }">
                <v-icon v-bind="props" size="x-small"
                  class="global-tooltip-icon mt-5 ml-n6">mdi-information-outline</v-icon>
              </template>
            </v-tooltip>
            <v-checkbox v-model="enableTools" @update:modelValue="toggleToolsUsage" label="Use Tools" hide-details density="compact"
              class="display-inline my-0 py-0" />
          </v-list-item>
          <v-list-item v-if="enableTools" density="compact"
            :class="showGlobalTooltips ? 'my-0 ml-6 mr-0 pa-0 mb-n1' : 'ma-0 pa-0 mb-n1'">
            <v-tooltip v-if="showGlobalTooltips" position="top" max-width="300"
              text="List of tools that the agent can use">
              <template v-slot:activator="{ props }">
                <v-icon v-bind="props" size="x-small"
                  class="global-tooltip-icon mt-5 ml-n6">mdi-information-outline</v-icon>
              </template>
            </v-tooltip>
            <v-select v-model="selectedTools" @update:modelValue="onToolSelectionChange" :items="toolsNameList"
              label="Tools" multiple chips density="compact" variant="outlined" :class="isOverlay ? 'mt-2 mb-n1' : 'mt-2'" />
          </v-list-item>
          <v-list-item v-if="enableTools && selectedTools.length > 0" density="compact" class="py-0 my-0">
            <v-btn variant="plain" @click="openFunctionSelectionDialog" size="small" class="ml-n2">
              Select Functions ({{ selectedFunctionsCount }} selected)
            </v-btn>
          </v-list-item>
          <!-- Embeddings -->
          <v-list-item density="compact" :class="showGlobalTooltips ? 'my-0 ml-6 mr-0 pa-0 mb-n1' : 'ma-0 pa-0 mb-n1'">
            <v-tooltip v-if="showGlobalTooltips" position="top" max-width="300"
              text="Enable the agent to look up information in data you've uploaded via the Embeddings tab.">
              <template v-slot:activator="{ props }">
                <v-icon v-bind="props" size="x-small"
                  class="global-tooltip-icon mt-5 ml-n6">mdi-information-outline</v-icon>
              </template>
            </v-tooltip>
            <v-checkbox v-model="enableEmbeddings" @update:modelValue="toggleEmbeddingsUsage"
              label="Use Embeddings (RAG)" hide-details density="compact" class="display-inline my-0 py-0" />
            <!-- <v-checkbox v-model="onlyEmbeddings" label="Only Use Embeddings" hide-details class="display-inline" /> -->
          </v-list-item>

          <v-list-item v-if="enableEmbeddings" density="compact"
            :class="showGlobalTooltips ? 'my-0 ml-6 mr-0 pa-0 mb-n1' : 'ma-0 pa-0 mb-n1'">
            <v-tooltip v-if="showGlobalTooltips" position="top" max-width="300"
              text="Datasets to include in the RAG (Retrieval-Augmented Generation) process. These are the datasets you've uploaded via the Embeddings tab.">
              <template v-slot:activator="{ props }">
                <v-icon v-bind="props" size="x-small"
                  class="global-tooltip-icon mt-5 ml-n6">mdi-information-outline</v-icon>
              </template>
            </v-tooltip>
            <v-select v-model="selectedEmbeddings" @update:modelValue="onEmbeddingCollectionChange"
              :items="embeddingsList" item-value="safeName" :class="isOverlay ? 'mt-2 mb-n1' : 'pt-2'" multiple chips item-title="name"
              label="Embeddings to include (RAG)" variant="outlined" density="compact" />
          </v-list-item>
          <v-list-item v-if="enableEmbeddings && showAdvanced" density="compact"
            :class="showGlobalTooltips ? 'my-0 ml-6 mr-0 pa-0 mb-n1' : 'ma-0 pa-0 mb-n1'">
            <v-tooltip v-if="showGlobalTooltips" position="top" max-width="300"
              text="The embedding specific model to use when looking up information you've uploaded. Don't change this unless you know what it is.">
              <template v-slot:activator="{ props }">
                <v-icon v-bind="props" size="x-small"
                  class="global-tooltip-icon mt-5 ml-n6">mdi-information-outline</v-icon>
              </template>
            </v-tooltip>
            <v-select v-model="embeddingModel" @update:modelValue="onEmbeddingModelChange"
              :items="effectiveEmbeddingModelConfigList" item-title="name" item-value="id" label="Embedding Model"
              :rules="[() => (!!embeddingModel && enableEmbeddings) || 'Embedding Model is required.']"
              density="compact" variant="outlined" :class="isOverlay ? 'display-block mt-2 mb-n1' : 'display-block pt-2'" />
          </v-list-item>
          <v-list-item v-if="enableEmbeddings" density="compact"
            :class="showGlobalTooltips ? 'my-0 ml-6 mr-0 pa-0 mb-n1' : 'ma-0 pa-0 mb-n1'">
            <v-tooltip v-if="showGlobalTooltips" position="top" max-width="300"
              text="This corresponds to the type of embeddings you're including and how you want the model to use them. Meta-prompts specific to these options are in your global config. Open = allow the agent to use it's own knowledge in addition, Closed = only use the embeddings, Code = specific prompting for code use and generation.">
              <template v-slot:activator="{ props }">
                <v-icon v-bind="props" size="x-small"
                  class="global-tooltip-icon mt-5 ml-n6">mdi-information-outline</v-icon>
              </template>
            </v-tooltip>
            <v-select v-model="embeddingType" @update:modelValue="onEmbeddingTypeChange" :items="embeddingTypes"
              :class="isOverlay ? 'mt-2' : 'pt-2'" item-title="name" label="Embedding type" variant="outlined" density="compact" />
          </v-list-item>

          <!-- Add Today's Date -->
          <!-- <v-list-item density="compact" :class="showGlobalTooltips ? 'my-0 ml-6 mr-0 pa-0' : 'ma-0 pa-0'">
            <v-tooltip v-if="showGlobalTooltips" position="top" max-width="300" text="Add today's date and time to the system prompt. This can be useful for agents that need to be aware of the current date.">
              <template v-slot:activator="{ props }">
                <v-icon v-bind="props" size="x-small" class="global-tooltip-icon mt-5 ml-n6">mdi-information-outline</v-icon>
              </template>
            </v-tooltip>
            <v-checkbox v-model="addToday" @update:modelValue="toggleAddDate" label="Add Today's Date to Prompt" hide-details class="display-inline" />
          </v-list-item> -->

          <!-- Timezone Selection -->
          <!-- <v-list-item v-if="addToday" density="compact" :class="showGlobalTooltips ? 'my-0 ml-6 mr-0 pa-0' : 'ma-0 pa-0'">
            <v-tooltip v-if="showGlobalTooltips" position="top" max-width="300" text="Select the timezone to use for the date and time added to the prompt.">
              <template v-slot:activator="{ props }">
                <v-icon v-bind="props" size="x-small" class="global-tooltip-icon mt-5 ml-n6">mdi-information-outline</v-icon>
              </template>
            </v-tooltip>
            <v-select v-model="timezoneOffset" :items="timezoneOptions" item-title="text" item-value="value"
              label="Timezone" density="compact" variant="outlined" class="pt-2" />
          </v-list-item> -->

          <!-- System Prompt Handling Options -->
          <v-list-item v-if="showAdvanced" density="compact"
            :class="showGlobalTooltips ? 'my-0 ml-6 mr-0 pa-0' : 'ma-0 pa-0'">
            <v-tooltip v-if="showGlobalTooltips" position="top" max-width="300"
              text="When enabled, the agent's system prompt will be combined with any system prompts passed in from external tools. The agent's prompt comes first.">
              <template v-slot:activator="{ props }">
                <v-icon v-bind="props" size="x-small" class="global-tooltip-icon mt-5 ml-n6">
                  mdi-information-outline
                </v-icon>
              </template>
            </v-tooltip>
            <v-checkbox v-model="combineSystemPrompts" @update:modelValue="toggleCombineSystemPrompts"
              label="Combine System Prompts" hide-details class="display-inline" />
          </v-list-item>

          <v-list-item v-if="showAdvanced" density="compact"
            :class="showGlobalTooltips ? 'my-0 ml-6 mr-0 pa-0' : 'ma-0 pa-0'">
            <v-tooltip v-if="showGlobalTooltips" position="top" max-width="300"
              text="When enabled, any additional system prompts from external tools will be converted to user messages instead of being treated as system prompts.">
              <template v-slot:activator="{ props }">
                <v-icon v-bind="props" size="x-small" class="global-tooltip-icon mt-5 ml-n6">
                  mdi-information-outline
                </v-icon>
              </template>
            </v-tooltip>
            <v-checkbox v-model="convertSystemPrompt" @update:modelValue="toggleConvertSystemPrompt"
              label="Convert System Prompts to User Messages" hide-details class="display-inline" />
          </v-list-item>

          <!-- Publish Agent -->
          <v-list-item v-if="!isShared && canPublishAgents" density="compact"
            :class="showGlobalTooltips ? 'my-0 ml-6 mr-0 pa-0 mb-n1' : 'ma-0 pa-0 mb-n1'">
            <v-tooltip v-if="showGlobalTooltips" position="top" max-width="300"
              text="Make this agent publicly accessible via a unique URL">
              <template v-slot:activator="{ props }">
                <v-icon v-bind="props" size="x-small" class="global-tooltip-icon mt-5 ml-n6">
                  mdi-information-outline
                </v-icon>
              </template>
            </v-tooltip>
            <div class="d-flex align-center">
              <v-checkbox v-model="isPublished" @change="togglePublish" label="Publish Agent" hide-details density="compact"
                class="display-inline my-0 py-0" />
              <Button v-if="show" color="primary" buttonClass="ml-2" :click="showShareDialog"
                icon="mdi-share-variant" size="medium" tooltip="Share Agent" />
              <v-tooltip v-if="isPublished" text="Open Public Page" location="top">
                <template v-slot:activator="{ props }">
                  <v-btn v-bind="props" color="secondary" icon="mdi-open-in-new" size="small"
                    class="ml-1 d-inline-flex agent-link" :href="publicUrl" @click="openPublicUrl"
                    variant="plain" target="_blank" />
                </template>
              </v-tooltip>
              <v-tooltip v-if="isPublished && publishedSlug" text="Chat with Agent" location="top">
                <template v-slot:activator="{ props }">
                  <v-btn v-bind="props" color="primary" icon="mdi-chat-outline" size="small"
                    class="ml-0 d-inline-flex agent-link" variant="plain"
                    @click="goToChat" />
                </template>
              </v-tooltip>
            </div>
          </v-list-item>
          <!-- <v-list-item v-else>
            <v-alert
              type="info"
              variant="tonal"
              density="compact"
              class="mb-3"
            >
              <v-icon size="small" class="mr-2">mdi-information</v-icon>
              Agent publishing is not enabled for your account. Contact support to enable this feature.
            </v-alert>
          </v-list-item> -->

          <!-- Show public URL when published -->
          <v-list-item v-if="!isShared && isPublished && publicUrl" density="compact" :class="isOverlay ? 'ma-0 pa-0 mt-2' : 'ma-0 pa-0'">
            <v-text-field :model-value="publicUrl" label="Public URL" readonly density="compact" variant="outlined" class="mt-2"
              append-inner-icon="mdi-content-copy" @click:append-inner="copyPublicUrl" />
          </v-list-item>

          <ModelOptionsChoices
            v-if="showAdvanced"
            @update="updateOptions"
            :modelOptionsInput="modelOptions"
            :modelName="modelConfig?.model || ''"
          />
        </v-list>
      </v-expand-transition>

      <v-card-actions :class="{ 'agent-editor-actions--overlay': isOverlay }">
        <Button v-if="show" color="primary" class="mr-4" type="submit" :disabled="saveDisabled" :click="saveAgent"
          icon="mdi-check-bold" size="x-large" tooltip="Save" />
        <Button v-if="show && !isShared" color="primary" class="mr-4" type="submit" :click="initiateDeleteAgent"
          icon="mdi-trash-can-outline" size="x-large" tooltip="Delete" />
        <template v-if="!show">
          <v-avatar size="28" rounded="lg" class="mr-3 collapsed-header-avatar agent-icon-tile"
            @click="toggleCardExpansion(true)">
            <v-icon :icon="displayIcon" size="small"></v-icon>
          </v-avatar>
          <v-tooltip :text="name" location="top" :open-delay="400">
            <template v-slot:activator="{ props }">
              <span v-bind="props" class="collapsed-header-name" @click="toggleCardExpansion(true)">{{ name }}</span>
            </template>
          </v-tooltip>
          <v-spacer @click="toggleCardExpansion(true)" class="collapsed-header-spacer" />
          <div class="collapsed-header-actions">
            <v-chip v-if="isPublished" color="success" size="x-small">LIVE</v-chip>
            <v-tooltip v-if="isPublished" text="Open Public Page" location="top">
              <template v-slot:activator="{ props }">
                <v-btn v-bind="props" color="secondary" icon="mdi-open-in-new" size="small"
                  class="agent-link" :href="publicUrl" variant="plain" target="_blank" />
              </template>
            </v-tooltip>
            <v-tooltip v-if="isPublished && publishedSlug" text="Chat with Agent" location="top">
              <template v-slot:activator="{ props }">
                <v-btn v-bind="props" color="primary" icon="mdi-chat-outline" size="small"
                  class="agent-link" variant="plain" @click="goToChat" />
              </template>
            </v-tooltip>
            <v-btn color="primary" icon="mdi-chevron-down"
              @click="toggleCardExpansion(true)"></v-btn>
          </div>
        </template>
        <template v-else>
          <v-spacer />
          <IconToggle :value="showAdvanced" @value="toggleAdvanced" highlightColor="primary" />
          <v-btn v-if="!isOverlay" color="primary" icon="mdi-chevron-up"
            @click="toggleCardExpansion(false)"></v-btn>
        </template>
      </v-card-actions>
    </v-form>
    <div v-if="waiting" class="card-action-overlay">
      <h2>{{ actionMessage }}</h2>
    </div>
    <ConfirmationDialog v-model="showDeleteConfirmationDialog" title="Confirm Agent Deletion"
      message="Are you sure you want to delete this agent configuration?" :itemName="agentNameToDelete" itemType="agent"
      :loading="waiting" @confirm="confirmAgentDelete" @cancel="cancelDeleteAgent" />
    <FunctionSelectionDialog :show="showFunctionSelectionDialog" :selected-tool-names="selectedTools"
      :initial-selected-functions="toolOptions.selectedFunctions || {}"
      :override-server-tools="isShared ? ownerToolsByServerName : null"
      @update:modelValue="showFunctionSelectionDialog = $event" @save="handleFunctionSelectionSave" />
    <AgentShareDialog v-if="!isShared" v-model="showShare" :ownerUsername="username" :slug="publishedSlug" :agentName="name" />
    <v-dialog v-model="showEmbeddingModelMismatchDialog" max-width="500">
      <v-card>
        <v-card-title class="text-h5">Embedding Model Mismatch</v-card-title>
        <v-card-text>
          Selected embedding collections must use the same embedding model. Incompatible collections have been removed.
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" variant="text" @click="showEmbeddingModelMismatchDialog = false">OK</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </component>
  <Teleport to="body">
    <div v-if="isPromptExpanded" class="prompt-overlay" @click="togglePromptExpansion"></div>
    <v-textarea v-if="isPromptExpanded" v-model="systemPrompt" @blur="trackFieldUpdate('agent_prompt')"
      :loading="loading" :disabled="loading" class="hide-scrollbar prompt-expanded" label="Agent Prompt"
      variant="outlined" hide-details density="compact">
      <template v-slot:append-inner>
        <div>
          <v-icon icon="mdi-arrow-collapse" color="primary"
            @click="togglePromptExpansion" class="expand-prompt-icon" style="cursor: pointer;"></v-icon>
        </div>
      </template>
    </v-textarea>
  </Teleport>
  </div>
</template>

<script lang="ts">
import { Options, mixins } from 'vue-class-component'
import { mapGetters, mapState } from 'vuex'
import AnalyticsMixin from '@/mixins/AnalyticsMixin'
import { AgentDefaults, AgentsState } from '@/store/agents'
import { ApiModel, ModelsState } from '@/store/models'
import Button from '@/components/sub/Button.vue'
import ModelOptionsChoices from '@/components/sub/ModelOptions.vue'
import IconToggle from '@/components/sub/IconToggle.vue'
import ConfirmationDialog from '@/components/dialogs/ConfirmationDialog.vue'
import FunctionSelectionDialog from '@/components/dialogs/FunctionSelectionDialog.vue'
import { TypedApiModel } from '@/types/general'
import { newId, sanitizeString } from '@/utils/general'
import AgentShareDialog from '@/components/AgentShareDialog.vue'
import { McpServer as StoreMcpServer } from '@/store/mcp'
import { AgentConfig, AgentToolOptions, SharedAgentInfo } from '@/types/agents'
import { ModelConfig, ModelOptions } from '@/types/models'
import { EmbeddingModelConfig, VectorStoreWithFiles } from '@/types/embeddings'
import type { VoiceConfig } from '@/types/voices'
import { DEFAULT_AGENT_ICON, resolveAgentIcon } from '@/utils/agentIcon'
import { getDashboardTabPath } from '@/router/dashboardTabs'
import { buildAgentToolOptions } from '@/utils/assistants'

@Options({
  props: {
    id: String,
    showTips: Boolean,
    isOverlay: Boolean,
    startExpanded: Boolean,
    sharedAgent: { type: Object, default: null }
  },
  components: {
    Button,
    ModelOptionsChoices,
    IconToggle,
    ConfirmationDialog,
    FunctionSelectionDialog,
    AgentShareDialog
  },
  computed: {
    ...mapState(['agents', 'models']),
    ...mapGetters(['username', 'apiModelsList', 'agentDefaults', 'embeddingModelConfigList', 'vectorStores', 'showGlobalTooltips', 'enabledServers', 'canPublishAgents']),
    tab() {
      return this.$store.state.global.tab
    },
    tabChanged() {
      return this.$store.state.global.tabChanged
    },
    agentConfig() {
      return this.agents.configs[this.id]
    }
  },
  emits: ['remove-card', 'saved'],
  watch: {
    agentConfig: {
      handler() {
        this.updateData()
      },
      deep: true
    },
    apiModelsList(models: ApiModel[]) {
      // console.log(models)
    },
    agentNames(list: string[]) {
      this.agentsList = list
    },
    tabChanged(changed) {
      if (this.tab === 'squads' && changed > this.lastActive) {
        this.updateData()
        this.lastActive = changed
      }
    }
  }
})
export default class AgentCard extends mixins(AnalyticsMixin) {
  id!: string
  username!: string
  isOverlay!: boolean
  startExpanded!: boolean
  sharedAgent!: SharedAgentInfo | null
  agentId: string = ''
  // Shared-mode owner-scoped catalogs and read-only model display
  sharedOwnerModelName: string = ''
  ownerEnabledServers: { name: string; displayName?: string; description?: string }[] = []
  ownerVectorStores: { name: string; safeName: string; embeddingModel: string }[] = []
  ownerEmbeddingModels: { id?: string; name: string; modelName: string; providerKey: string }[] = []
  ownerVoiceNames: string[] = []
  ownerToolsByServerName: Record<string, { name: string; description?: string; inputSchema?: any }[]> = {}
  agentHashChanged!: (config: AgentConfig) => Promise<boolean>
  hashChanged: boolean = false
  agents!: AgentsState
  agentNames!: string[]
  agentsList: string[] = []
  agentDefaults!: AgentDefaults
  models!: ModelsState
  public showGlobalTooltips!: boolean
  name: string = ''
  description: string = ''
  icon: string | null = ''
  iconInput: string = ''
  showIconPicker = false
  timestamp: number = 0
  model: string | null = null
  modelConfig: ModelConfig | null = null
  apiModelsList!: TypedApiModel[]
  systemPrompt: string = ''
  sendContextToAgent: string = ''
  showAdvanced: boolean = false
  temperature: number = 0.1
  tab!: string
  lastActive: number = Date.now()
  loading: boolean = false
  maxTokens: number = -1
  isNewAgent: boolean = true
  show: boolean = false
  modelOptions: ModelOptions = {}
  isPromptExpanded = false
  canPublishAgents!: boolean
  // tools
  public enableTools = false
  public enabledServers!: StoreMcpServer[]
  public selectedTools: string[] = [] // Names of selected MCP servers
  public toolOptions: AgentToolOptions = { systemTools: [], userTools: [], selectedFunctions: {} }
  public showFunctionSelectionDialog = false
  
  // embeddings
  public embeddingModelConfigList!: EmbeddingModelConfig[]
  public embeddingModel: string | null = null
  public vectorStores!: Record<string, VectorStoreWithFiles>
  public selectedEmbeddings: string[] = []
  public enableEmbeddings: boolean = false
  public onlyEmbeddings: boolean = false
  public embeddingType: string | null = null
  public embeddingTypes: string[] = ['open', 'closed', 'code']
  public waiting = false
  public actionMessage = ''
  public isPublished = false
  public publicUrl = ''
  public showShare = false
  public publishedSlug = ''

  // NEW: per-agent voice and optional response processor
  public voiceSavedName: string | null = null
  public responseProcessorAgent: string | null = null
  
  public addToday = false
  public timezoneOffset = '-500' // Default to Eastern Time
  public combineSystemPrompts = false
  public convertSystemPrompt = false
  public timezoneOptions = [
    { text: 'Eastern Time (UTC-5:00)', value: '-500' },
    { text: 'Central Time (UTC-6:00)', value: '-600' },
    { text: 'Mountain Time (UTC-7:00)', value: '-700' },
    { text: 'Pacific Time (UTC-8:00)', value: '-800' },
    { text: 'UTC', value: '+000' },
    { text: 'Central European Time (UTC+1:00)', value: '+100' },
    { text: 'Eastern European Time (UTC+2:00)', value: '+200' },
    { text: 'India Standard Time (UTC+5:30)', value: '+530' },
    { text: 'China Standard Time (UTC+8:00)', value: '+800' },
    { text: 'Japan Standard Time (UTC+9:00)', value: '+900' },
    { text: 'Australian Eastern Time (UTC+10:00)', value: '+1000' }
  ]

  public showDeleteConfirmationDialog = false
  public agentNameToDelete: string | null = null
  public showEmbeddingModelMismatchDialog = false
  private initialized = false

  get defaultAgentIcon(): string {
    return DEFAULT_AGENT_ICON
  }

  get isShared(): boolean {
    return this.sharedAgent != null
  }

  get displayIcon(): string {
    return resolveAgentIcon(this.normalizedIconValue ?? this.icon)
  }

  get iconSelectionValue(): string {
    return this.normalizedIconValue || DEFAULT_AGENT_ICON
  }

  get normalizedIconValue(): string | undefined {
    const trimmed = (this.iconInput ?? '').trim()
    return trimmed.length > 0 ? trimmed : undefined
  }

  get mdiBrowseUrl(): string {
    return 'https://pictogrammers.com/library/mdi/'
  }

  get iconPresets(): string[] {
    return [
      'mdi-source-pull',
      'mdi-rss',
      'mdi-finance',
      'mdi-account-supervisor-outline',
      'mdi-database',
      'mdi-phone-outline',
      'mdi-pencil-outline',
      'mdi-card-text-outline',
      'mdi-code-json',
      'mdi-robot-outline',
      'mdi-shield-check-outline',
      'mdi-flask-outline'
    ]
  }

  created() {
    this.temperature = this.agents.defaults.options.temperature ?? 0.2
    this.agentsList = this.agentNames
    console.log(this.apiModelsList)
  }

  toggleAdvanced(v: boolean) {
    this.trackButtonClick('toggle_advanced_options', v ? 'show' : 'hide', { agent_id: this.agentId });
    this.showAdvanced = v
  }

  togglePromptExpansion() {
    if (this.isOverlay) {
      return
    }
    this.isPromptExpanded = !this.isPromptExpanded;
    this.trackButtonClick('toggle_agent_prompt', this.isPromptExpanded ? 'expand' : 'collapse', { agent_id: this.agentId });
  }

  async mounted() {
    if (this.isShared) {
      await this.loadSharedAgent()
    } else {
      this.updateData()
    }
  }

  async loadSharedAgent(): Promise<void> {
    if (!this.sharedAgent) return
    this.show = true
    this.initialized = true
    const ownerUsername = this.sharedAgent.ownerUsername
    const slug = this.sharedAgent.slug

    const [catalogs, sharedResult] = await Promise.all([
      this.$store.dispatch('fetchSharedAgentOwnerCatalogs', { ownerUsername, slug }) as Promise<{
        tools: { servers: any[]; tools: any[] } | null
        embeddings: { vectorStores: any[]; embeddingModels: any[] } | null
        voices: { voices: { name: string }[] } | null
      }>,
      this.$store.dispatch('fetchSharedAgentConfig', { ownerUsername, slug }) as Promise<{ config: AgentConfig; modelName: string } | null>
    ])

    if (catalogs?.tools) {
      this.ownerEnabledServers = catalogs.tools.servers ?? []
      this.ownerToolsByServerName = {}
      for (const obj of catalogs.tools.tools ?? []) {
        for (const [sName, tools] of Object.entries(obj as Record<string, any[]>)) {
          this.ownerToolsByServerName[sName] = tools
        }
      }
    }
    if (catalogs?.embeddings) {
      this.ownerVectorStores = catalogs.embeddings.vectorStores ?? []
      this.ownerEmbeddingModels = catalogs.embeddings.embeddingModels ?? []
    }
    if (catalogs?.voices) {
      this.ownerVoiceNames = (catalogs.voices.voices ?? []).map((v) => v.name)
    }

    if (sharedResult != null) {
      const c = sharedResult.config
      this.agentId = c.id
      this.timestamp = c.timestamp
      this.name = c.name
      this.description = c.description
      this.icon = typeof c.icon === 'string' ? c.icon : ''
      this.iconInput = this.icon ?? ''
      this.systemPrompt = c.systemPrompt
      // Server resolves the friendly model name; fall back to the raw value if missing.
      this.sharedOwnerModelName = sharedResult.modelName || c.model || ''
      // Keep `model` set to the owner's stored value so the `config` getter produces
      // a complete AgentConfig (it short-circuits to undefined when model is null).
      // The model <v-select> is hidden in shared mode, so this value is never reflected
      // through the recipient's local models catalog.
      this.model = c.model ?? null
      this.modelConfig = null
      this.temperature = c.temperature
      this.maxTokens = c.maxTokens ?? -1
      this.modelOptions = c.modelOptions ?? { temperature: 0.2 }
      if (c.embeddingOptions) {
        this.embeddingModel = c.embeddingOptions.model
        this.selectedEmbeddings = c.embeddingOptions.collections
        this.embeddingType = c.embeddingOptions.type
        this.enableEmbeddings = true
      }
      if (c.toolOptions) {
        this.toolOptions = {
          systemTools: c.toolOptions.systemTools || [],
          userTools: c.toolOptions.userTools || [],
          selectedFunctions: c.toolOptions.selectedFunctions || {}
        }
        this.enableTools = this.toolOptions.userTools.length > 0
        this.selectedTools = this.toolOptions.userTools
      } else {
        this.toolOptions = { systemTools: [], userTools: [], selectedFunctions: {} }
      }
      if (c.addToday !== undefined) this.addToday = c.addToday
      if (c.timezoneOffset !== undefined) this.timezoneOffset = c.timezoneOffset
      if (c.combineSystemPrompts !== undefined) this.combineSystemPrompts = c.combineSystemPrompts
      if (c.convertSystemPrompt !== undefined) this.convertSystemPrompt = c.convertSystemPrompt
      this.voiceSavedName = c.voice?.savedVoiceName ?? null
      this.responseProcessorAgent = c.responseProcessorAgent ?? null
      this.isPublished = !!c.publishedInfo?.isPublished
      this.publicUrl = c.publishedInfo?.publicUrl ?? ''
      this.publishedSlug = c.publishedInfo?.slug ?? ''
      this.isNewAgent = false
    }
  }

  // --- CARD LIFECYCLE & VISIBILITY ---
  toggleCardExpansion(isShown: boolean) {
    if (this.isOverlay && !isShown) {
      return
    }
    this.show = isShown;
    this.trackButtonClick('toggle_agent_card', isShown ? 'expand' : 'collapse', { agent_id: this.agentId });
  }

  selectIconPreset(iconName: string) {
    this.icon = iconName
    this.iconInput = iconName
    this.showIconPicker = false
    this.trackFieldUpdate('agent_icon')
  }

  commitIconInput() {
    this.icon = this.normalizedIconValue ?? ''
    this.showIconPicker = false
    this.iconInput = this.icon ?? ''
    this.trackFieldUpdate('agent_icon')
  }

  // --- FIELD EDITS (on blur) ---
  trackFieldUpdate(fieldName: string) {
    this.trackFormInteraction('agent_form', 'update_field', fieldName);
    this.checkHash(); // Existing logic
  }

  // --- TOOLS ---
  toggleToolsUsage(enabled: boolean | null) {
    this.trackFeatureUsage('agent_tools', 'toggle_usage', { agent_id: this.agentId, enabled: !!enabled });
  }

  onToolSelectionChange(selectedTools: string[]) {
    this.trackFeatureUsage('agent_tools', 'select_tools', { agent_id: this.agentId, tools: selectedTools });
  }

  // --- EMBEDDINGS (RAG) ---
  toggleEmbeddingsUsage(enabled: boolean | null) {
    this.trackFeatureUsage('agent_embeddings', 'toggle_usage', { agent_id: this.agentId, enabled: !!enabled });
  }

  onEmbeddingModelChange(modelId: string) {
    this.trackFeatureUsage('agent_embeddings', 'select_model', { agent_id: this.agentId, embedding_model_id: modelId });
  }

  onEmbeddingCollectionChange(collections: string[]) {
    this.trackFeatureUsage('agent_embeddings', 'select_collections', { agent_id: this.agentId, collections });

    if (collections.length === 0) {
      this.embeddingModel = null;
      this.embeddingType = null;
      return;
    }

    const firstCollectionSafeName = collections[0];
    const firstVectorStore = this.embeddingsList.find(vs => vs.safeName === firstCollectionSafeName);
    const referenceModel = firstVectorStore?.embeddingModel;

    if (!referenceModel) {
      // Handle case where the first selected item has no model, though this shouldn't happen with valid data.
      this.selectedEmbeddings = [];
      this.embeddingModel = null;
      this.embeddingType = null;
      return;
    }

    const validCollections = collections.filter(safeName => {
      const vectorStore = this.embeddingsList.find(vs => vs.safeName === safeName);
      return vectorStore && vectorStore.embeddingModel === referenceModel;
    });

    if (validCollections.length < collections.length) {
      this.trackEvent('embedding_model_mismatch', { agent_id: this.agentId });
      this.showEmbeddingModelMismatchDialog = true;
    }

    this.selectedEmbeddings = validCollections;

    if (this.selectedEmbeddings.length > 0) {
      const matchingModel = this.embeddingModelConfigList.find(
        modelConfig => modelConfig.name.toLowerCase() === referenceModel.toLowerCase()
      );
      if (matchingModel) {
        this.embeddingModel = matchingModel.id ?? null;
      }
      this.embeddingType = 'open';
    } else {
      // This case would be hit if the initial selection was cleared due to mismatch
      this.embeddingModel = null;
      this.embeddingType = null;
    }
  }

  onEmbeddingTypeChange(type: string) {
    this.trackFeatureUsage('agent_embeddings', 'select_type', { agent_id: this.agentId, type });
  }

  // --- OTHER SETTINGS ---
  toggleAddDate(enabled: boolean | null) {
    this.trackFeatureUsage('agent_settings', 'toggle_add_date', { agent_id: this.agentId, enabled: !!enabled });
  }

  toggleCombineSystemPrompts(enabled: boolean | null) {
    this.trackFeatureUsage('agent_settings', 'toggle_combine_system_prompts', { 
      agent_id: this.agentId, 
      enabled: !!enabled 
    });
    if (enabled) {
      this.convertSystemPrompt = false;
    }
  }

  toggleConvertSystemPrompt(enabled: boolean | null) {
    this.trackFeatureUsage('agent_settings', 'toggle_convert_system_prompt', { 
      agent_id: this.agentId, 
      enabled: !!enabled 
    });
    if (enabled) {
      this.combineSystemPrompts = false;
    }
  }

  private getModelConfig(key: string): ModelConfig | undefined {
    console.log('AgentCard, getModelConfig', key)
    if (this.models.configs[key] != null) {
      return this.models.configs[key].config
    }
    const agentModelConfig = this.models.configsById[key]
    if (agentModelConfig != null) {
      return agentModelConfig.config
    }
    console.error('AgentCard, getModelConfig, model not found', key)
    return undefined
  }

  updateData() {
    if (this.isShared) return
    const agentConfig = this.agents.configs[this.id]
    if (agentConfig != null && this.models != null) {
      const config = agentConfig.config
      const { id, timestamp, name, description, model, systemPrompt, sendContextToAgent, temperature, maxTokens, modelOptions, embeddingOptions, toolOptions, addToday, timezoneOffset, combineSystemPrompts, convertSystemPrompt } = config
      console.log('AgentCard, updateData, config', config)
      this.agentId = id
      this.timestamp = timestamp
      this.name = name
      this.description = description
      this.icon = typeof config.icon === 'string' ? config.icon : ''
      this.iconInput = this.icon ?? ''
      const loadedModelConfig = this.getModelConfig(model) 
      if (loadedModelConfig != null) {
        this.modelConfig = loadedModelConfig
        this.model = loadedModelConfig.id
      }
      
      this.systemPrompt = systemPrompt
      if (sendContextToAgent != null) {
        this.sendContextToAgent = sendContextToAgent
      }
      this.temperature = temperature
      this.maxTokens = maxTokens ?? -1
      this.isNewAgent = false
      if (modelOptions != null) {
        this.modelOptions = modelOptions
      } else if (this.modelConfig != null && this.modelConfig.options != null && Object.keys(this.modelConfig.options).length > 0) {
        this.modelOptions = this.modelConfig.options
      } else {
        this.modelOptions = this.agentDefaults.options ?? { temperature: 0.2 }
      }
      if (embeddingOptions != null) {
        this.embeddingModel = embeddingOptions.model
        this.selectedEmbeddings = embeddingOptions.collections
        this.embeddingType = embeddingOptions.type
        this.enableEmbeddings = true
      }
      if (toolOptions != null) {
        this.toolOptions = {
          systemTools: toolOptions.systemTools || [],
          userTools: toolOptions.userTools || [],
          selectedFunctions: toolOptions.selectedFunctions || {}
        }
        this.enableTools = this.toolOptions.userTools.length > 0
        this.selectedTools = this.toolOptions.userTools
      } else {
        this.toolOptions = { systemTools: [], userTools: [], selectedFunctions: {} }
      }
      
      if (addToday !== undefined) {
        this.addToday = addToday
      }
      if (timezoneOffset !== undefined) {
        this.timezoneOffset = timezoneOffset
      }
      if (combineSystemPrompts !== undefined) {
        this.combineSystemPrompts = combineSystemPrompts
      }
      if (convertSystemPrompt !== undefined) {
        this.convertSystemPrompt = convertSystemPrompt
      }
      if (config.publishedInfo) {
        this.isPublished = config.publishedInfo.isPublished;
        this.publicUrl = config.publishedInfo.publicUrl;
        this.publishedSlug = (config as any).publishedInfo?.slug || '';
      } else {
        this.isPublished = false;
        this.publicUrl = '';
        this.publishedSlug = '';
      }

      // NEW: initialize optional voice and processor from config
      if (config.voice?.savedVoiceName) {
        this.voiceSavedName = config.voice.savedVoiceName
      } else {
        this.voiceSavedName = null
      }
      if (config.responseProcessorAgent) {
        this.responseProcessorAgent = config.responseProcessorAgent
      } else {
        this.responseProcessorAgent = null
      }
      if (!this.initialized) {
        this.show = !!this.startExpanded
        this.initialized = true
      }
    }
    if (agentConfig == null && !this.id) {
      this.iconInput = this.icon ?? ''
      this.show = true
      this.initialized = true
    }
  }

  showShareDialog() {
    this.showShare = true
  }

  goToChat(): void {
    if (!this.publishedSlug) return
    this.$store.dispatch('setPendingChatAgent', { slug: this.publishedSlug })
    this.$store.dispatch('setTab', 'chat')
    const path = getDashboardTabPath('chat')
    if (this.$route.path !== path) {
      this.$router.push(path)
    }
  }

  modelSelected() {
    // When the model is changed, update the modelConfig and save the agent
    if (this.model) {
      this.trackFeatureUsage('agent', 'select_model', { agent_id: this.agentId, model_id: this.model });
      const selectedModel = this.models.configsById[this.model]
      if (selectedModel != null) {
        this.modelConfig = selectedModel.config as ModelConfig
        if (!this.isNewAgent && !this.saveDisabled) {
          this.saveAgent()
        }
      }
    }
  }

  updateOptions(options: ModelOptions) {
    this.modelOptions = options
    if (!this.isNewAgent && !this.saveDisabled) {
      this.saveAgent()
    }
  }

  get config(): AgentConfig | undefined {
    if (this.model != null) {
      const config: AgentConfig = {
        id: this.agentId === '' ? newId() : this.agentId,
        timestamp: this.timestamp > 0 ? this.timestamp : Date.now(),
        name: this.name,
        description: this.description,
        icon: this.normalizedIconValue || (this.icon ?? '').trim() || undefined,
        systemPrompt: this.systemPrompt,
        model: this.model, // This should be the user-configured model name (ID from ModelConfig)
        context: [],
        temperature: this.temperature,
        maxTokens: parseInt(this.maxTokens.toString(), 10) ?? -1,
        modelOptions: this.modelOptions,
        addToday: this.addToday,
        timezoneOffset: this.addToday ? this.timezoneOffset : undefined,
        combineSystemPrompts: this.combineSystemPrompts,
        convertSystemPrompt: this.convertSystemPrompt
      }
      if (this.enableEmbeddings && this.embeddingModel != null && this.embeddingType != null && this.selectedEmbeddings.length > 0) {
        config.embeddingOptions = {
          model: this.embeddingModel,
          collections: this.selectedEmbeddings,
          type: this.embeddingType
        }
      }
      if (this.enableTools && this.selectedTools.length > 0) {
        // Shared with the simple-mode assistant editor — see buildAgentToolOptions in
        // src/utils/assistants.ts. Advanced deliberately does not prune selectedFunctions.
        config.toolOptions = buildAgentToolOptions({
          systemTools: this.toolOptions.systemTools,
          userTools: this.selectedTools,
          selectedFunctions: this.toolOptions.selectedFunctions || {}
        })
      } else {
        config.toolOptions = undefined
      }

      // Preserve publishedInfo if it exists
      const existingConfig = this.agents.configs[this.id]?.config
      if (existingConfig?.publishedInfo) {
        config.publishedInfo = existingConfig.publishedInfo
      }

      // NEW: persist optional voice and processor selections
      if (this.voiceSavedName && this.voiceSavedName.trim().length > 0) {
        config.voice = { savedVoiceName: this.voiceSavedName.trim() }
      }
      if (this.responseProcessorAgent && this.responseProcessorAgent.trim().length > 0) {
        config.responseProcessorAgent = this.responseProcessorAgent.trim()
      }
      
      return config
    }
    return undefined
  }

  get saveDisabled(): boolean {
    return !this.name.trim() || !this.description.trim() || !this.systemPrompt.trim() || !this.model
  }

  get toolsNameList(): string[] {
    if (this.isShared) {
      return this.ownerEnabledServers.map(s => s.name)
    }
    if (this.enabledServers != null) {
      return this.enabledServers.map(s => s.name)
    }
    return []
  }

  get selectedFunctionsCount(): number {
    if (this.toolOptions?.selectedFunctions) {
      return Object.values(this.toolOptions.selectedFunctions).reduce((acc, funcs) => acc + (funcs?.length || 0), 0)
    }
    return 0
  }

  get embeddingsList(): Array<{ name: string; safeName: string; embeddingModel?: string }> {
    if (this.isShared) {
      return this.ownerVectorStores
    }
    if (this.vectorStores != null) {
      return Object.values(this.vectorStores).map(vs => ({
        ...vs,
        safeName: sanitizeString(vs.name)
      }));
    }
    return []
  }

  get effectiveEmbeddingModelConfigList(): EmbeddingModelConfig[] {
    if (this.isShared) {
      return this.ownerEmbeddingModels as EmbeddingModelConfig[]
    }
    return this.embeddingModelConfigList
  }

  // NEW: available saved voice alias names (from voices store, or owner catalog when shared)
  get savedVoiceNames(): string[] {
    if (this.isShared) {
      return [...this.ownerVoiceNames].sort((a, b) => a.localeCompare(b))
    }
    const byName = this.$store.getters.voiceConfigs as Record<string, VoiceConfig> | undefined
    if (!byName) return []
    return Object.keys(byName).sort((a, b) => a.localeCompare(b))
  }

  // NEW: processor agent choices (exclude self)
  get processorAgentChoices(): string[] {
    const names = this.agentNames || []
    return names.filter((n) => n !== this.name).sort((a, b) => a.localeCompare(b))
  }

  get modelChoices(): ModelConfig[] {
    if (this.models?.configsById == null) {
      return []
    }
    return Object.values(this.models.configsById).map((model) => model.config)
  }

  async checkHash() {
    const config = this.config
    if (this.agentId !== '' && config != null) {
      // this.hashChanged = await this.agentHashChanged(config)
    }
  }

  async togglePublish() {
    this.trackButtonClick('toggle_agent_publish', 'click', { agent_id: this.agentId, is_published: !this.isPublished });
    await this.$store.dispatch('toggleAgentPublish', { agentId: this.agentId, agentName: this.name });
  }

  copyPublicUrl() {
    this.trackButtonClick('copy_public_url', 'click', { agent_id: this.agentId });
    navigator.clipboard.writeText(this.publicUrl);
    this.$store.dispatch('updateSnackbar', { text: 'Public URL copied to clipboard!', timeout: 3000 });
  }
  
  openPublicUrl() {
    this.trackButtonClick('open_public_url', 'click', { agent_id: this.agentId });
  }

  async saveAgent() {
    const isCreating = this.isNewAgent;
    this.trackButtonClick(isCreating ? 'create_agent' : 'update_agent', 'save', { agent_id: this.agentId || 'new' });
    const config = this.config
    console.log('save agent', config)
    if (config == null || (!this.isShared && this.saveDisabled)) {
      this.$store.dispatch('updateSnackbar', {
        text: 'Cannot save: Missing required fields.',
        timeout: 3000
      })
      return
    }

    if (this.isShared && this.sharedAgent) {
      const payload = {
        description: config.description,
        icon: config.icon,
        systemPrompt: config.systemPrompt,
        modelOptions: config.modelOptions,
        addToday: config.addToday,
        timezoneOffset: config.timezoneOffset,
        combineSystemPrompts: config.combineSystemPrompts,
        convertSystemPrompt: config.convertSystemPrompt,
        toolOptions: config.toolOptions,
        embeddingOptions: config.embeddingOptions,
        voice: this.voiceSavedName ? { savedVoiceName: this.voiceSavedName } : null
      }
      this.waiting = true
      this.actionMessage = 'Saving...'
      const success = await this.$store.dispatch('updateSharedAgentConfig', {
        ownerUsername: this.sharedAgent.ownerUsername,
        slug: this.sharedAgent.slug,
        payload,
        agentName: this.sharedAgent.agentName
      })
      this.waiting = false
      this.actionMessage = ''
      if (success) {
        this.$emit('saved', { name: this.name })
      }
      return
    }

    this.waiting = true
    this.actionMessage = 'Saving...'
    if (this.sendContextToAgent != null && this.sendContextToAgent !== '') {
      config.sendContextToAgent = this.sendContextToAgent
    }
    const success = await this.$store.dispatch('setAgentConfig', { config, previousName: this.id }) // Pass previousName to handle rename
    this.waiting = false
    this.actionMessage = ''

    if (!success) {
      return
    }

    this.agentId = config.id
    this.$store.dispatch('sendTelemetry', {
      type: 'setAgentConfig',
      data: config
    })
    this.isNewAgent = false
    this.$store.dispatch('updateSnackbar', { text: `Saved agent ${this.name}`, timeout: 3_000 })
    this.$emit('saved', { name: this.name, id: this.agentId })
  }

  initiateDeleteAgent() {
    this.trackButtonClick('delete_agent_start', 'click', { agent_id: this.agentId });
    if (this.isNewAgent || this.agentId === '') {
      this.$emit('remove-card')
      return
    }
    this.agentNameToDelete = this.name
    this.showDeleteConfirmationDialog = true
  }

  async confirmAgentDelete() {
    this.trackButtonClick('delete_agent_confirm', 'confirm', { agent_id: this.agentId });
    if (this.agentNameToDelete) {
      this.waiting = true
      this.actionMessage = 'Deleting...'
      const success = await this.$store.dispatch('deleteAgentConfig', this.agentNameToDelete)
      this.waiting = false
      this.actionMessage = ''
      if (success) {
        this.$store.dispatch('updateSnackbar', { text: `Deleted agent ${this.agentNameToDelete}`, timeout: 3_000 })
        this.$emit('remove-card')
      } else {
        this.$store.dispatch('updateSnackbar', { text: `Failed to delete agent ${this.agentNameToDelete}`, timeout: 3_000, color: 'error' })
      }
    }
    this.cancelDeleteAgent()
  }

  cancelDeleteAgent() {
    this.trackButtonClick('delete_agent_cancel', 'cancel', { agent_id: this.agentId });
    this.showDeleteConfirmationDialog = false
    this.agentNameToDelete = null
  }

  openFunctionSelectionDialog() {
    this.trackButtonClick('open_function_selection', 'click', { agent_id: this.agentId });
    if (!this.toolOptions) {
      this.toolOptions = { systemTools: [], userTools: this.selectedTools, selectedFunctions: {} };
    } else if (!this.toolOptions.selectedFunctions) {
      this.toolOptions.selectedFunctions = {};
    }
    this.showFunctionSelectionDialog = true
  }

  handleFunctionSelectionSave(selectedFunctions: { [serverName: string]: string[] }) {
    this.trackFeatureUsage('agent_tools', 'save_functions', { 
      agent_id: this.agentId, 
      function_count: Object.values(selectedFunctions).reduce((acc, funcs) => acc + (funcs?.length || 0), 0)
    });
    if (!this.toolOptions) {
      this.toolOptions = { systemTools: [], userTools: this.selectedTools, selectedFunctions: {} };
    }
    this.toolOptions.selectedFunctions = selectedFunctions
    this.showFunctionSelectionDialog = false
    // Optionally, immediately save the agent or wait for explicit save action
    this.saveAgent() 
  }

  async autofillPrompt() {
    this.trackButtonClick('autofill_agent_prompt', 'click', { agent_id: this.agentId, model: this.model });
    if (this.model == null) {
      return
    }
    this.loading = true
    let { content, role } = this.agentDefaults.prompt
    if (typeof content !== 'string') {
      this.$store.dispatch('updateSnackbar', {
        text: 'Cannot autofill prompt with complex content.',
        color: 'error',
        timeout: 3000
      });
      this.loading = false;
      return;
    }
    const prompt = content.replace('<name>', this.name).replace('<description>', this.description)
    this.$store.dispatch('updateSnackbar', {
      text: `Sent autofill request for ${this.name} description`,
      timeout: 3_000
    })
    try {
      const response: string = await this.$store.dispatch('generatePrompt', {
        prompt,
        modelConfig: this.modelConfig,
        options: this.modelOptions
      })
      this.trackEvent('autofill_agent_prompt_success', { agent_id: this.agentId });
      this.systemPrompt = response.replace(/<think>[\s\S]*?<\/think>/g, '')
      while (this.systemPrompt.startsWith('\n')) {
        this.systemPrompt = this.systemPrompt.substring(2)
      }
    } catch (error: any) {
      this.trackEvent('autofill_agent_prompt_failure', { agent_id: this.agentId, reason: error.message });
    }
    
    this.loading = false
  }
}
</script>

<style scoped lang="scss">
.card {
  border: 1px solid rgba(255, 255, 255, 0.3);
}
.shared-agent-banner {
  padding: 12px 16px;
  :deep(.v-alert__content) {
    color: rgb(var(--v-theme-on-surface));
    line-height: 1.4;
  }
}
.agent-editor-surface--overlay {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
}
.agent-editor-surface--overlay > form {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
  overflow: hidden;
}
.autocomplete-icon {
  position: absolute;
  right: 4px;
  top: 8px;
}
.v-text-field.smaller-text ::v-deep {
  input {
    font-size: 0.9rem;
  }
}
.display-inline {
  display: inline-block;
}
.v-textarea {
  :deep(.v-field--appended) {
    padding-inline-end: 0;
  }

  :deep(textarea) {
    padding-right: 16px;
  }
}

.expand-prompt-icon {
  position: absolute;
  bottom: 8px;
  right: 8px;
  z-index: 5;
}

.agent-link {
  height: 24px !important;
  width: 24px !important;
  min-width: 24px !important;
}

.collapsed-header-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
  cursor: pointer;
  // Fluid font: 0.875rem at 320px viewport, scales to 1.25rem at 1600px
  font-size: clamp(0.875rem, 0.6rem + 0.8vw, 1.25rem);
  font-weight: 500;
  line-height: 1.5;
}

.collapsed-header-spacer {
  cursor: pointer;
}

.collapsed-header-actions {
  display: flex;
  align-items: center;
  flex-shrink: 0;
  gap: 2px;
}
.collapsed-header-avatar {
  cursor: pointer;
  flex-shrink: 0;
}
.agent-icon-tile {
  background-color: #1f1d16;
  color: #fff;
}
.agent-icon-picker-anchor {
  position: relative;
  display: inline-flex;
  align-items: flex-start;
}
.agent-icon-picker-trigger {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 22px !important;
  height: 22px !important;
  min-width: 22px !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.22);
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.18s ease;
}
.agent-icon-picker-anchor:hover .agent-icon-picker-trigger {
  opacity: 1;
  pointer-events: auto;
}
.agent-icon-picker-menu {
  width: 272px;
  padding: 12px;
  border: 1px solid rgba(0, 0, 0, 0.08);
}
.agent-icon-picker-grid {
  display: grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  gap: 6px;
}
.agent-icon-option {
  min-width: 32px !important;
}
.agent-icon-picker-divider {
  height: 1px;
  margin: 12px 0 10px;
  background: rgba(0, 0, 0, 0.08);
}
.agent-icon-picker-label {
  font-size: 0.7rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: rgba(0, 0, 0, 0.5);
}
.agent-icon-picker-link {
  margin-top: 10px;
  display: inline-flex;
  align-items: center;
  font-size: 0.82rem;
  color: rgba(var(--v-theme-primary), 1);
  text-decoration: none;
}
.agent-icon-picker-link:hover {
  text-decoration: underline;
}
.agent-editor-header {
  min-height: 36px;
  padding: 8px 0 2px;
}

.agent-overlay-layout {
  display: flex;
  flex: 1 1 auto;
  min-height: 0;
  overflow: hidden;
}

.agent-overlay-row {
  flex: 1 1 auto;
  min-height: 0;
}

.agent-overlay-settings,
.agent-overlay-prompt-column {
  min-height: 0;
}

.agent-overlay-settings {
  max-height: 100%;
  overflow-x: hidden;
  overflow-y: auto;
  padding-bottom: 12px !important;
}

.agent-overlay-prompt {
  min-height: 0;
}

.agent-overlay-prompt :deep(.v-input),
.agent-overlay-prompt :deep(.v-input__control),
.agent-overlay-prompt :deep(.v-field),
.agent-overlay-prompt :deep(.v-field__field),
.agent-overlay-prompt :deep(.v-field__input) {
  height: 100%;
  min-height: 0;
}

.agent-overlay-prompt :deep(.v-field__input) {
  align-items: stretch;
}

.agent-overlay-prompt :deep(textarea) {
  height: 100% !important;
  min-height: 0 !important;
  font-family: var(--font-mono, monospace);
  line-height: 1.55;
}

.agent-editor-actions--overlay {
  padding: 8px 26px !important;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
  background: rgb(var(--v-theme-background));
}
</style>

<style lang="scss">
/* Unscoped styles for Teleported prompt overlay/expansion (rendered outside component DOM) */
.prompt-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.7);
  z-index: 2000;
}

.v-textarea.prompt-expanded {
  position: fixed !important;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 70vw !important;
  height: 60vh !important;
  z-index: 2001;
  background-color: rgb(var(--v-theme-background));
  padding: 1rem;
  border-radius: 8px;

  .v-field__control, .v-field__field {
    height: 100%;
  }

  textarea {
    height: 100% !important;
    resize: none !important;
  }

  .v-field {
    height: 57vh;
    margin-top: 8px;
    padding-bottom: 0 !important;
  }

  .v-input__details {
    display: none !important;
  }

  .v-field--appended {
    padding-inline-end: 0;
  }

  .expand-prompt-icon {
    position: absolute;
    bottom: 8px;
    right: 8px;
    z-index: 5;
  }
}
</style>
